import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { BufferLoading } from '~/shared-components';
import { actions as orgActions } from '~/organizations';
import { getOrgIdFromRoute } from '~/app-pages';
import MissingAccessPage from '../../../missing-access-page/index';
import { useAccount } from '../../../accountContext';

const LoadingGateLoader = styled.div`
  width: 100vw;
  display: flex;
  justify-content: center;
  height: inherit;
  align-items: center;
`;

const LoadingGate = ({
  reduxReady,
  children,
  storeSelectedOrg,
  hasCurrentOrg,
  profiles,
  currentPath,
}) => {
  const user = useAccount();
  const selectedOrgInAccount = user?.account?.currentOrganization?.id;
  const canAccessPublishing =
    user?.account?.currentOrganization?.billing?.canAccessPublishing;
  const orgIdFromRoute = getOrgIdFromRoute({ currentPath, profiles });
  // we are using hasCurrentOrg in this check to make sure this can only be true on the initial rendering
  const needsToSelectNewOrgInOrgSwitcher =
    selectedOrgInAccount !== orgIdFromRoute &&
    !!orgIdFromRoute &&
    !hasCurrentOrg;

  // Update the organization in Redux on user organization change
  useEffect(() => {
    if (reduxReady && selectedOrgInAccount && hasCurrentOrg) {
      storeSelectedOrg(selectedOrgInAccount);
    }
  }, [selectedOrgInAccount, hasCurrentOrg, reduxReady, storeSelectedOrg]);

  if (!user.loading && canAccessPublishing === false) {
    return <MissingAccessPage />;
  }

  // select the organization in Redux once we have fetched the user
  // this is only used in the first render
  if (reduxReady && !user?.loading && selectedOrgInAccount && !hasCurrentOrg) {
    // If org coming from route doesn't match the last org stored, select and store the new value
    // this only applies for the initial render otherwise the account is driving
    // This only applies if route has a valid org (not a null, undefined or empty value).
    if (needsToSelectNewOrgInOrgSwitcher) {
      const { actions } = window?.appshell || {};
      actions.setCurrentOrganization(orgIdFromRoute);
    } else {
      storeSelectedOrg(selectedOrgInAccount);
    }
  }

  return (
    <>
      {reduxReady && !user?.loading && !needsToSelectNewOrgInOrgSwitcher ? (
        children
      ) : (
        <LoadingGateLoader>
          <BufferLoading size={64} />
        </LoadingGateLoader>
      )}
    </>
  );
};

LoadingGate.propTypes = {
  reduxReady: PropTypes.bool,
  children: PropTypes.node.isRequired,
  storeSelectedOrg: PropTypes.func.isRequired,
  hasCurrentOrg: PropTypes.bool.isRequired,
  currentPath: PropTypes.string.isRequired,
  profiles: PropTypes.array.isRequired,
};

LoadingGate.defaultProps = {
  reduxReady: false,
};

/**
 * We use this component to delay rendering children until we have
 * enough data loaded into the state.
 */
export default connect(
  (state) => {
    const orgsLoaded = state.organizations.loaded;
    const userLoaded = state.user.loaded;
    const profilesLoaded = state.profileSidebar.loaded;
    const hasCurrentOrg = !!state.organizations?.selected;
    const currentPath = state?.router?.location?.pathname;
    const profiles = state?.profileSidebar?.profileList;

    return {
      reduxReady: orgsLoaded && userLoaded && profilesLoaded,
      hasCurrentOrg,
      currentPath,
      profiles,
    };
  },
  (dispatch) => ({
    storeSelectedOrg: (currentOrgId) => {
      dispatch(orgActions.setCurrentOrganization(currentOrgId));
    },
  })
)(LoadingGate);
